import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { BarChart3 as PasspStatsLogoIcon } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'react-toastify';
import api from '../services/api';

const IT_MONTHS = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];

const currentYear = new Date().getFullYear();

export default function PasspFattureStats() {
  const [azienda, setAzienda] = useState('all');
  const [year, setYear] = useState(currentYear);
  const [aziendeOptions, setAziendeOptions] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [monthlyData, setMonthlyData] = useState([]);

  const yearOptions = useMemo(
    () => Array.from({ length: 6 }).map((_, idx) => currentYear - idx),
    []
  );

  const formatCurrency = (value) => {
    if (value == null) return '-';
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return num.toLocaleString('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    });
  };

  const fetchFilters = async () => {
    setLoadingFilters(true);
    try {
      const res = await api.get('/passp-fatture/filters/options');
      const data = res.data || {};
      setAziendeOptions((data.aziende || []).filter(Boolean));
    } catch (error) {
      console.error('Error fetching Passpartout invoices filters (stats):', error);
      toast.error('Errore nel caricamento dei filtri Fatture Passpartout');
    } finally {
      setLoadingFilters(false);
    }
  };

  const fetchMonthlyStats = async () => {
    setLoadingData(true);
    try {
      const promises = [];

      for (let m = 0; m < 12; m += 1) {
        const fromDate = new Date(year, m, 1);
        const toDate = new Date(year, m + 1, 0);
        const dateFrom = fromDate.toISOString().slice(0, 10);
        const dateTo = toDate.toISOString().slice(0, 10);

        const params = { dateFrom, dateTo };
        if (azienda !== 'all') {
          params.azienda = azienda;
        }

        promises.push(
          api
            .get('/passp-fatture/stats', { params })
            .then((res) => {
              const d = res.data || {};
              return {
                monthIndex: m,
                total: d.total || 0,
                totalAmount: d.totalAmount || 0,
                totalFT: d.totalFT || 0,
                totalNC: d.totalNC || 0,
                amountFT: d.amountFT || 0,
                amountNC: d.amountNC || 0,
              };
            })
            .catch((error) => {
              console.error(
                `Error fetching stats for month ${m + 1}/${year}:`,
                error
              );
              return {
                monthIndex: m,
                total: 0,
                totalAmount: 0,
                totalFT: 0,
                totalNC: 0,
                amountFT: 0,
                amountNC: 0,
              };
            })
        );
      }

      const results = await Promise.all(promises);
      const ordered = results
        .slice()
        .sort((a, b) => a.monthIndex - b.monthIndex)
        .map((item) => ({
          monthIndex: item.monthIndex,
          monthLabel: IT_MONTHS[item.monthIndex].slice(0, 3),
          documents: Number(item.total) || 0,
          amount: Number(item.totalAmount) || 0,
          ftDocuments: Number(item.totalFT) || 0,
          ncDocuments: Number(item.totalNC) || 0,
          ftAmount: Number(item.amountFT) || 0,
          ncAmount: Number(item.amountNC) || 0,
        }));

      setMonthlyData(ordered);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchMonthlyStats();
  }, [azienda, year]);

  const totalDocsYear = monthlyData.reduce(
    (sum, m) => sum + (Number(m.documents) || 0),
    0
  );
  const totalAmountYear = monthlyData.reduce(
    (sum, m) => sum + (Number(m.amount) || 0),
    0
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PasspStatsLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}
            >
              Statistiche Movimenti
            </Typography>
            <Typography variant="body2" color="text.secondary">
              KPI mensili Fatture Passpartout per azienda e anno selezionati.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow:
            '0 0 0 1px rgba(15,23,42,0.05), 0 8px 16px rgba(15,23,42,0.06)',
        }}
      >
        <CardHeader
          title={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Filtri
              </Typography>
              {loadingFilters && <CircularProgress size={16} />}
            </Box>
          }
        />
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Azienda</InputLabel>
              <Select
                label="Azienda"
                value={azienda}
                onChange={(e) => setAzienda(e.target.value)}
              >
                <MenuItem value="all">Tutte le aziende</MenuItem>
                {aziendeOptions.map((opt) => (
                  <MenuItem key={opt.code} value={opt.code}>
                    {opt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Anno</InputLabel>
              <Select
                label="Anno"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 2.5,
        }}
      >
        <Card
          sx={{
            borderRadius: 2,
            boxShadow:
              '0 0 0 1px rgba(15,23,42,0.05), 0 12px 24px rgba(15,23,42,0.08)',
          }}
        >
          <CardHeader
            title={
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Andamento mensile documenti e volume
              </Typography>
            }
            subheader={
              <Typography variant="body2" color="text.secondary">
                Numero documenti (barre) e totale € (linea) per mese
              </Typography>
            }
          />
          <CardContent sx={{ height: 340 }}>
            {loadingData ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress />
              </Box>
            ) : monthlyData.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Nessun dato disponibile per i filtri selezionati.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={monthlyData}
                  margin={{ top: 20, right: 40, left: 0, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#e5e7eb"
                  />
                  <XAxis
                    dataKey="monthLabel"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) =>
                      v >= 1000000
                        ? `${(v / 1000000).toFixed(1)}M€`
                        : `${(v / 1000).toFixed(0)}k€`
                    }
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(15,23,42,0.12)',
                      fontSize: 12,
                    }}
                    formatter={(value, name) => {
                      if (name === 'Documenti') return [value, 'Documenti'];
                      if (name === 'Volume') return [formatCurrency(value), 'Volume'];
                      return [value, name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    yAxisId="left"
                    dataKey="documents"
                    name="Documenti"
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="amount"
                    name="Volume"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: 2,
            boxShadow:
              '0 0 0 1px rgba(15,23,42,0.05), 0 8px 16px rgba(15,23,42,0.08)',
          }}
        >
          <CardHeader
            title={
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                KPI annuali
              </Typography>
            }
          />
          <CardContent>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                rowGap: 1.2,
              }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Documenti totali
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {totalDocsYear}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Volume complessivo
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrency(totalAmountYear)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Importo medio documento
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {totalDocsYear > 0
                    ? formatCurrency(totalAmountYear / totalDocsYear)
                    : 'N/D'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Card
        sx={{
          mt: 3,
          borderRadius: 2,
          boxShadow:
            '0 0 0 1px rgba(15,23,42,0.05), 0 6px 12px rgba(15,23,42,0.06)',
        }}
      >
        <CardHeader
          title={
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Dettaglio mensile
            </Typography>
          }
        />
        <CardContent>
          {loadingData ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 3,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mese</TableCell>
                  <TableCell align="right">Numero documenti</TableCell>
                  <TableCell align="right">Totale €</TableCell>
                  <TableCell align="right">FT (n.)</TableCell>
                  <TableCell align="right">FT (€)</TableCell>
                  <TableCell align="right">NC (n.)</TableCell>
                  <TableCell align="right">NC (€)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {IT_MONTHS.map((label, idx) => {
                  const item = monthlyData.find((m) => m.monthIndex === idx) || {
                    documents: 0,
                    amount: 0,
                    ftDocuments: 0,
                    ncDocuments: 0,
                    ftAmount: 0,
                    ncAmount: 0,
                  };
                  return (
                    <TableRow key={label}>
                      <TableCell>{label}</TableCell>
                      <TableCell align="right">{item.documents}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.amount)}
                      </TableCell>
                      <TableCell align="right">
                        {item.ftDocuments}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.ftAmount)}
                      </TableCell>
                      <TableCell align="right">
                        {item.ncDocuments}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.ncAmount)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
